import type { ComponentInit, RegistryEntry } from "./registry";
import { allowsMotion } from "./motion";
import { selectOne } from "./registry";

const NAV_MENU = "[data-fa-nav-menu]";
const SITE_HEADER = "[data-fa-site-header]";
const COUNTDOWN = "[data-fa-countdown]";
const LOGO_TICKER = "[data-fa-logo-ticker]";
const FOOTER_BOUNCE = "[data-fa-bounce]";
const SMOOTH_SCROLL = "[data-fa-smooth-scroll]";
const SUBSCRIBE = "[data-fa-subscribe]";
const SHOP_CALLOUT = "[data-fa-shop-callout]";
const DIVIDER_SHOULDER = "[data-fa-divider-shoulder]";
const FEATURED_ARTICLES = "[data-fa-featured-articles]";
const HEAD_TO_HEAD_FORM = "[data-fa-head-to-head-tile-form]";
const FEATURED_VIDEO = "[data-fa-featured-video]";
const ARTICLE_LISTING = "[data-fa-article-listing]";
const SQUAD_LISTING = "[data-fa-squad-listing]";
const FIXTURE_BREAKDOWN = "[data-fa-fixture-breakdown]";
const VIDEO = "[data-fa-video]";
const SPONSOR_BLOCK = "[data-fa-sponsor-block]";
const ARTICLE_HERO = "[data-fa-article-hero]";
const HERO_VIDEO_LAYER = "[data-fa-hero-video-layer]";
const SEARCH_BAR = "[data-fa-search-bar]";
const CASCADE = "[data-fa-cascade]";
const UNDERLINE_LINES = "[data-fa-underline-lines]";
const BREADCRUMB = "[data-fa-breadcrumb]";
const MEDIA_PLAYER = "[data-fa-media-player]";
const TAB_BAR = "[data-fa-tab-bar]";
const MODAL_TRIGGER = "[data-fa-modal-trigger]";
const FILTER_DROP = "[data-fa-filter-drop]";
const TABS = "[data-fa-tabs]";

const wantsSmoothScroll = () =>
	window.matchMedia("(hover: hover) and (pointer: fine)").matches && allowsMotion();

const noop: ComponentInit = () => undefined;

/* Never removed: `load()` runs before anything tracks teardowns, so `once` is the only handle. */
const onFirstFocus = () =>
	new Promise<void>((resolve) => {
		document.addEventListener("focusin", () => resolve(), { once: true, capture: true });
	});

export const entries: readonly RegistryEntry[] = [
	{
		selector: BREADCRUMB,
		load: () =>
			import("../ui/Composites/Breadcrumb/Breadcrumb.scripts").then((m) => m.initBreadcrumb),
	},
	{
		selector: NAV_MENU,
		load: () => import("../ui/Composites/NavMenu/NavMenu.scripts").then((m) => m.initNavMenu),
	},
	{
		selector: SITE_HEADER,
		load: () =>
			import("../ui/Globals/SiteHeader/SiteHeader.scripts").then((m) => m.initSiteHeader),
	},
	{
		selector: COUNTDOWN,
		load: () =>
			import("../ui/Composites/Countdown/Countdown.scripts").then((m) => m.initCountdown),
	},
	{
		selector: LOGO_TICKER,
		load: () =>
			allowsMotion()
				? import("../ui/Regions/LogoTicker/LogoTicker.scripts").then(
						(m) => m.initLogoTicker,
					)
				: Promise.resolve(noop),
	},
	{
		selector: FOOTER_BOUNCE,
		load: () =>
			allowsMotion()
				? import("../ui/Globals/SiteFooter/SiteFooter.scripts").then(
						(m) => m.initSiteFooterBounce,
					)
				: Promise.resolve(noop),
	},
	{
		selector: SUBSCRIBE,
		load: () =>
			onFirstFocus().then(() =>
				import("../ui/Composites/SubscribeCallout/SubscribeCallout.scripts").then(
					(m) => m.initSubscribeCallout,
				),
			),
	},
	{
		selector: SHOP_CALLOUT,
		load: () =>
			import("../ui/Blocks/ShopCallout/ShopCallout.scripts").then((m) => m.initShopCallout),
	},
	{
		/* Not an event binding, and unlike VIDEO and CASCADE it cannot be gated in `load()`: a
		   shoulder matching the neighbouring block is not a preference [SCRIPTS-4]. */
		selector: DIVIDER_SHOULDER,
		load: () => import("./dividerShoulder").then((m) => m.initDividerShoulder),
	},
	{
		selector: HEAD_TO_HEAD_FORM,
		load: () =>
			import("../ui/Regions/MatchCentre/MatchCentreContent/Tiles/HeadToHeadTile/HeadToHeadTile.scripts").then(
				(m) => m.initHeadToHeadForm,
			),
	},
	{
		selector: TAB_BAR,
		load: () => import("../ui/Composites/TabBar/TabBar.scripts").then((m) => m.initTabBar),
	},
	{
		selector: TABS,
		load: () => import("./tabs").then((m) => m.initTabs),
	},
	{
		selector: MODAL_TRIGGER,
		load: () => import("./modal").then((m) => m.initModal),
	},
	{
		selector: FILTER_DROP,
		load: () =>
			import("../ui/Composites/FilterDrop/FilterDrop.scripts").then((m) => m.initFilterDrop),
	},
	{
		selector: FEATURED_ARTICLES,
		load: () =>
			import("../ui/Blocks/FeaturedArticles/FeaturedArticles.scripts").then(
				(m) => m.initFeaturedArticles,
			),
	},
	{
		selector: FEATURED_VIDEO,
		load: () =>
			import("../ui/Blocks/FeaturedVideo/FeaturedVideo.scripts").then(
				(m) => m.initFeaturedVideo,
			),
	},
	{
		selector: ARTICLE_LISTING,
		load: () =>
			import("../ui/Blocks/ArticleListing/ArticleListing.scripts").then(
				(m) => m.initArticleListing,
			),
	},
	{
		selector: SQUAD_LISTING,
		load: () =>
			import("../ui/Blocks/SquadListing/SquadListing.scripts").then(
				(m) => m.initSquadListing,
			),
	},
	{
		selector: FIXTURE_BREAKDOWN,
		load: () =>
			import("../ui/Regions/FixtureBreakdown/FixtureBreakdown.scripts").then(
				(m) => m.initFixtureBreakdown,
			),
	},
	{
		selector: VIDEO,
		load: () => import("../ui/Primitives/Video/Video.scripts").then((m) => m.initVideo),
	},
	{
		selector: SPONSOR_BLOCK,
		load: () =>
			import("../ui/Blocks/SponsorBlock/SponsorBlock.scripts").then(
				(m) => m.initSponsorBlock,
			),
	},
	{
		selector: ARTICLE_HERO,
		load: () =>
			import("../ui/Heroes/ArticleHero/ArticleHero.scripts").then((m) => m.initArticleHero),
	},
	{
		selector: HERO_VIDEO_LAYER,
		load: () => import("../ui/Heroes/HeroVideo.scripts").then((m) => m.initHeroVideo),
	},
	{
		selector: UNDERLINE_LINES,
		load: () => import("./lineSplit").then((m) => m.initLineSplit),
	},
	{
		/* Not an event binding, like VIDEO above: gated in `load()`, so a reader who wants no
		   motion fetches nothing [SCRIPTS-5]. RevealWord stamps `data-fa-cascade` on a standalone
		   word-reveal heading too, so this one entry owns every split — nested or not. */
		selector: CASCADE,
		load: () =>
			allowsMotion() ? import("./cascade").then((m) => m.initCascade) : Promise.resolve(noop),
	},
	{
		selector: MEDIA_PLAYER,
		load: () =>
			import("../ui/Composites/MediaPlayer/MediaPlayer.scripts").then(
				(m) => m.initMediaPlayer,
			),
	},
	{
		selector: SEARCH_BAR,
		load: () =>
			import("../ui/Composites/SearchBar/SearchBar.scripts").then((m) => m.initSearchBar),
	},
	{
		selector: SMOOTH_SCROLL,
		select: selectOne(SMOOTH_SCROLL),
		scope: "document",
		load: () =>
			wantsSmoothScroll()
				? import("./smoothScroll").then((m) => m.initSmoothScroll)
				: Promise.resolve(noop),
	},
];
